import { useMemo, type ReactNode } from 'react';
import type { AutomationRule } from '@hygge/shared';
import { summarizeRule } from '@hygge/shared';
import { useStatuses, useProjectTags } from '@/hooks/use-tasks';
import { useAssignableMembers } from '@/hooks/use-boards';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { Zap, Filter, ArrowRight, Ban } from 'lucide-react';
import { cn } from '@/lib/utils';

function FlowNode({
  marker,
  icon,
  label,
  children,
}: {
  marker: string;
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="relative flex items-start gap-3.5 py-1.5">
      <div
        className={cn(
          'relative z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white',
          marker
        )}
      >
        {icon}
      </div>
      <div className="min-w-0 flex-1 rounded-lg border border-border bg-background p-3">
        <div className="mb-1 text-xs font-medium text-muted-foreground">{label}</div>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  );
}

export function FlowView({ boardId, rule }: { boardId: string; rule: AutomationRule }) {
  const { data: statuses = [] } = useStatuses(boardId);
  const { data: tags = [] } = useProjectTags(boardId);
  const members = useAssignableMembers(boardId) ?? [];
  const formatName = useEmployeeNameFormatter();

  const summary = useMemo(() => {
    const lookups = {
      status: Object.fromEntries(statuses.map((s) => [s.id, s.name])),
      tag: Object.fromEntries(tags.map((t) => [t.id, t.name])),
      member: Object.fromEntries(
        members.map((m) => [m.employeeId, formatName({ firstName: m.firstName, lastName: m.lastName })])
      ),
    };
    return summarizeRule(rule, lookups);
  }, [rule, statuses, tags, members, formatName]);

  return (
    <div className="relative py-2">
      {/* Continuous rail threading the node markers */}
      <div className="absolute bottom-7 left-[17px] top-7 w-0.5 bg-border" aria-hidden="true" />

      <FlowNode marker="bg-hygge" icon={<Zap className="h-4 w-4" />} label="When this happens">
        {summary.when}
      </FlowNode>

      <FlowNode
        marker="bg-amber-500"
        icon={<Filter className="h-4 w-4" />}
        label={summary.alwaysRuns ? 'Always' : `If ${summary.match} of these match`}
      >
        {summary.alwaysRuns ? (
          <span className="text-muted-foreground">Runs on every matching trigger</span>
        ) : (
          <>
            {summary.conditions.map((c, i) => (
              <div key={i} className="leading-6">
                {c}
              </div>
            ))}
            <div className="mt-2 flex items-center gap-1.5 border-t border-dashed border-border pt-2 text-xs text-muted-foreground">
              <Ban className="h-3 w-3 shrink-0" /> Otherwise — stop, no actions run
            </div>
          </>
        )}
      </FlowNode>

      {summary.actions.map((a, i) => (
        <FlowNode
          key={i}
          marker="bg-hygge"
          icon={<ArrowRight className="h-4 w-4" />}
          label={summary.actions.length > 1 ? `Then · step ${i + 1}` : 'Then do this'}
        >
          {a}
        </FlowNode>
      ))}
    </div>
  );
}
